// Vertrag erstellen - die Teile ohne Oberfläche (seit 23.09.2026).
// 1. Aus VertragsAmPendingBoard.jsx herausgelöst, Zeichen für Zeichen (Vertragsart
//    aus dem Deal, Anzeige-Reparatur, Suche, Warteschlange); was aus dem Studio
//    kommt, steht in vertragsStand.js. Desktop und Handy nutzen dieselben Funktionen.
// 2. Neu für das Handy: Deals filtern, Zähler, Sperrgründe, Stand der Vorlage und die
//    Nutzlast für Vorschau und Abschließen (Form wie handleRunPreview und
//    handleFinalizeContract im Studio). Nachträge ("In Bitrix leer") gehen nur als
//    wörtlicher Wert in mapping.fields dieser einen Anfrage (resolveRuntimeMappingValue
//    in api/contracts/usage.js) - nie in Bitrix oder ins gespeicherte Mapping.

import {
  KLEINSPEICHER_LEASE_ANNEX_KEY,
  KLEINSPEICHER_LEASE_ANNEX_NUMBER,
  KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ,
  KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ,
  KLEINSPEICHER_PURCHASE_CONTRACT_KEY,
  LEASE_NUMBER_INITIAL_VALUES,
  buildLeaseNumberMappingFields,
  getLeaseNumberRequirements,
  getMissingLeaseNumberLabels,
  mergeMappingFieldsPreferFilled,
} from './kleinspeicherLeaseAnnex';
import {
  ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY,
  BUYER_FIELD_PLACEHOLDERS,
  LAGEPLAN_PLACEHOLDER,
  LAGEPLAN_PLACEHOLDER_TOKEN,
  MAX_CONTRACT_ATTACHMENT_BYTES,
  applyBuyerManualFieldsToHtml,
  applyUploadedContractToVariantTemplate,
  buildContractStateKey,
  buildMappingFieldsWithClauseDefaults,
  cleanAdditionalOwnersForPayload,
  createAttachmentId,
  extractClausePlaceholders,
  extractPlaceholders,
  getLageplanImageInfo,
  mergePlaceholders,
  normalizeAttachmentForPayload,
  normalizeClauseDefaultMapping,
  normalizePlaceholderName,
  removeBuyerFieldsFromMapping,
  replaceLageplanImageSource,
} from './vertragsStand';
import { ersetzungenWort } from './gespraech';
import { istWerkvertrag, mitGrundzuordnung, wendeWerkvertragOptionenAn } from './werkvertrag';

// ===========================================================================
// 1. Aus dem Deal-Board (VertragsAmPendingBoard.jsx)
// ===========================================================================

export function readStoredManualUploadBatchId() {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY) || '';
  } catch (_error) {
    return '';
  }
}

export function isActiveManualUploadBatch(batch) {
  if (!batch || batch.done === true) return false;
  return Number(batch.pending || 0) > 0 || Number(batch.running || 0) > 0;
}

export function normalizeContractSelectionText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const CP1252_REVERSE_BYTES = new Map([
  [0x20AC, 0x80], [0x201A, 0x82], [0x0192, 0x83], [0x201E, 0x84],
  [0x2026, 0x85], [0x2020, 0x86], [0x2021, 0x87], [0x02C6, 0x88],
  [0x2030, 0x89], [0x0160, 0x8A], [0x2039, 0x8B], [0x0152, 0x8C],
  [0x017D, 0x8E], [0x2018, 0x91], [0x2019, 0x92], [0x201C, 0x93],
  [0x201D, 0x94], [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97],
  [0x02DC, 0x98], [0x2122, 0x99], [0x0161, 0x9A], [0x203A, 0x9B],
  [0x0153, 0x9C], [0x017E, 0x9E], [0x0178, 0x9F],
]);

const CP1252_MOJIBAKE_TRAIL = '\u0080-\u00bf\u20ac\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u017D\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02dc\u2122\u0161\u203A\u0153\u017E\u0178';
const MOJIBAKE_TEXT_PATTERN = new RegExp(`(?:\\u00c3[${CP1252_MOJIBAKE_TRAIL}]|\\u00c2[${CP1252_MOJIBAKE_TRAIL}]|\\u00e2[\\u0080-\\uffff]{2})+`, 'gu');
const MOJIBAKE_HINT_PATTERN = /[\u00c2\u00c3\u00e2]/u;
const CONTROL_TEXT_PATTERN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/u;

function getCp1252Byte(character) {
  if (!character) return null;
  const code = character.codePointAt(0);
  if (code <= 0xff) return code;
  return CP1252_REVERSE_BYTES.get(code) ?? null;
}

function decodeCp1252MojibakeSegment(segment) {
  const bytes = [];
  for (const character of segment) {
    const byte = getCp1252Byte(character);
    if (byte === null) return segment;
    bytes.push(byte);
  }

  const decoded = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(bytes));
  if (!decoded || decoded === segment || decoded.includes('\uFFFD') || CONTROL_TEXT_PATTERN.test(decoded)) {
    return segment;
  }
  return decoded;
}

export function repairDisplayText(value) {
  if (typeof value !== 'string') return String(value == null ? '' : value);
  if (!MOJIBAKE_HINT_PATTERN.test(value)) return value;

  let next = value;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const repaired = next.replace(MOJIBAKE_TEXT_PATTERN, decodeCp1252MojibakeSegment);
    if (repaired === next) break;
    next = repaired;
    if (!MOJIBAKE_HINT_PATTERN.test(next)) break;
  }
  return next;
}

export function detectPaymentKind(...values) {
  const text = values.map(normalizeContractSelectionText).join(' ');
  if (/(^|[^a-z0-9])(508|480|jz|jaehrlich|jaehrliche|jahreszahlung|jahrespacht|pacht jaehrlich|annual)([^a-z0-9]|$)/i.test(text)) {
    return 'jz';
  }
  if (/(^|[^a-z0-9])(510|482|ez|einmal|einmalig|einmalige|einmalzahlung|einmalpacht|vorabpacht|one time|one-time)([^a-z0-9]|$)/i.test(text)) {
    return 'ez';
  }
  return '';
}

export function resolveContractKeyFromPendingDeal(item) {
  const typeValue = String(item?.typeValue || '').trim();
  const markerText = [item?.dealTitle, item?.typeLabel, item?.paymentLabel, item?.paymentTypeLabel]
    .map(normalizeContractSelectionText)
    .join(' ');
  const kleinspeicherPaymentCode = String(item?.kleinspeicherPaymentCode || '').trim();
  const pvPaymentCode = String(item?.pvPaymentCode || '').trim();
  const kleinspeicherPaymentKind = detectPaymentKind(kleinspeicherPaymentCode, item?.kleinspeicherPaymentLabel, markerText);
  const pvPaymentKind = detectPaymentKind(pvPaymentCode, item?.pvPaymentLabel, markerText);

  const isBessDeal = ['496', '500', '534'].includes(typeValue)
    || markerText.includes('bess')
    || markerText.includes('großspeicher')
    || markerText.includes('grossspeicher');
  const isFfpvDeal = typeValue === '590'
    || markerText.includes('ffpv')
    || markerText.includes('freifläche')
    || markerText.includes('freiflaeche');

  if (isBessDeal || isFfpvDeal) return '';
  if (typeValue === '532' || markerText.includes('sanierung')) return 'dachnutzungsvertrag-sanierung';

  const isStorage = ['698', '702'].includes(typeValue)
    || markerText.includes('kleinspeicher')
    || markerText.includes('schwarmspeicher');
  if (isStorage) {
    if (kleinspeicherPaymentKind === 'jz') return 'pachtvertrag-kleinspeicher-jz';
    if (kleinspeicherPaymentKind === 'ez') return 'pachtvertrag-kleinspeicher-ez';
    return 'pachtvertrag-kleinspeicher-jz';
  }

  const isPv = typeValue === '498'
    || markerText.includes('pv-angebot')
    || markerText.includes('dach-pv')
    || markerText.includes('dach pv');
  if (isPv) {
    if (pvPaymentKind === 'jz') return 'pachtvertrag-dachverpachtung-jz';
    if (pvPaymentKind === 'ez') return 'pachtvertrag-dachverpachtung-ez';
  }

  return '';
}

export function resolveKleinspeicherLeaseContractKey(item, fallbackContractKey = KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ) {
  const paymentCode = String(item?.kleinspeicherPaymentCode || '').trim();
  const paymentKind = detectPaymentKind(paymentCode, item?.kleinspeicherPaymentLabel, item?.dealTitle, item?.typeLabel);
  if (paymentKind === 'jz') return KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ;
  if (paymentKind === 'ez') return KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ;
  return fallbackContractKey;
}

export function normalizeSearchValue(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function buildPendingDealSearchText(item, assignedContractLabel = '') {
  return [
    item?.dealId,
    item?.dealTitle,
    item?.phaseId,
    item?.phaseLabel,
    item?.contact,
    item?.responsible,
    item?.typeLabel,
    item?.typeValue,
    item?.size,
    item?.kleinspeicherPaymentCode,
    item?.pvPaymentCode,
    assignedContractLabel,
  ].map(normalizeSearchValue).filter(Boolean).join(' ');
}

export function normalizePendingDealForDisplay(item) {
  if (!item || typeof item !== 'object') return item;
  return {
    ...item,
    dealTitle: repairDisplayText(item.dealTitle),
    phaseLabel: repairDisplayText(item.phaseLabel),
    contact: repairDisplayText(item.contact),
    responsible: repairDisplayText(item.responsible),
    typeLabel: repairDisplayText(item.typeLabel),
    kleinspeicherPaymentLabel: repairDisplayText(item.kleinspeicherPaymentLabel),
    pvPaymentLabel: repairDisplayText(item.pvPaymentLabel),
  };
}

export function normalizePendingPhaseForDisplay(phase) {
  if (!phase || typeof phase !== 'object') return phase;
  return {
    ...phase,
    phaseLabel: repairDisplayText(phase.phaseLabel),
  };
}

// ===========================================================================
// 2. Neu für die mobile Oberfläche
// ===========================================================================

const VERMITTLERVERTRAG = 'vermittlervertrag';
const PROJEKTRECHTE = 'projektrechte-pv';
export const DEAL_ARTEN = ['Alle', 'Dach-PV', 'Kleinspeicher', 'Sanierung', 'Großspeicher', 'FFPV'];
export const ANLAGEN_ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

export const SCHRITTE = [
  { nr: 1, kurz: 'Vertrag', titel: 'Welcher Vertrag?', text: 'Vertragsart und Variante festlegen.' },
  { nr: 2, kurz: 'Daten', titel: 'Daten aus Bitrix', text: 'Felder holen und Lücken nachtragen.' },
  { nr: 3, kurz: 'Ergänzen', titel: 'Was kommt dazu?', text: 'Paragraphen, Eigentümer, Anlagen – alles optional.' },
  { nr: 4, kurz: 'Prüfen', titel: 'Alles richtig?', text: 'Antippen, um etwas zu ändern.' },
];

const liste = (wert) => (Array.isArray(wert) ? wert : []);
const text = (wert) => String(wert ?? '').trim();

// ---------- Deals ----------

/**
 * Art eines Deals für die Chips - aus denselben Merkmalen, an denen
 * resolveContractKeyFromPendingDeal die Vertragsart erkennt (Typ-Kennung aus
 * Bitrix, sonst Stichworte in Titel und Typ). '' = passt zu keinem Chip.
 */
export function dealArt(item) {
  const typ = text(item?.typeValue);
  const merkmale = [item?.dealTitle, item?.typeLabel, item?.paymentLabel, item?.paymentTypeLabel]
    .map(normalizeContractSelectionText).join(' ');
  const hat = (...worte) => worte.some((w) => merkmale.includes(w));
  if (['496', '500', '534'].includes(typ) || hat('bess', 'grossspeicher')) return 'Großspeicher';
  if (typ === '590' || hat('ffpv', 'freiflaeche')) return 'FFPV';
  if (typ === '532' || hat('sanierung')) return 'Sanierung';
  if (['698', '702'].includes(typ) || hat('kleinspeicher', 'schwarmspeicher')) return 'Kleinspeicher';
  if (typ === '498' || hat('pv-angebot', 'dach-pv', 'dach pv')) return 'Dach-PV';
  return '';
}

/**
 * Welche Deals schon in der Upload-Warteschlange stehen: Aufträge des
 * laufenden Stapels (wie das Deal-Board ihn über op=am-upload-queue lädt) plus
 * die am Handy gerade abgeschlossenen. Ist ein Auftrag fehlgeschlagen, zählt
 * der Deal wieder als offen.
 */
export function dealsInWarteschlange({ batch = null, lokal = [] } = {}) {
  const jobs = batch && Array.isArray(batch.jobs) ? batch.jobs : [];
  const fehlgeschlagen = new Set(jobs.filter((j) => j?.status === 'failed').map((j) => text(j?.dealId)));
  const ids = new Set(liste(lokal).map(text).filter((id) => id && !fehlgeschlagen.has(id)));
  if (isActiveManualUploadBatch(batch)) {
    jobs.forEach((job) => {
      if ((job?.status === 'pending' || job?.status === 'running') && text(job?.dealId)) ids.add(text(job.dealId));
    });
  }
  return ids;
}

/** Suche (alle Wörter müssen passen, "D_7196" und "#7196" finden die ID), Chip, Sortierung. */
export function filtereDeals(items, { suche = '', art = 'Alle', warteschlange = new Set(), vertragsName = () => '' } = {}) {
  const teile = normalizeSearchValue(suche).split(/\s+/).map((t) => t.replace(/^(d_|#)/, '')).filter(Boolean);
  const wartet = (item) => (warteschlange.has(text(item?.dealId)) ? 1 : 0);
  return liste(items)
    .filter((item) => art === 'Alle' || dealArt(item) === art)
    .filter((item) => {
      if (!teile.length) return true;
      const heuhaufen = buildPendingDealSearchText(item, `${vertragsName(resolveContractKeyFromPendingDeal(item)) || ''} ${dealArt(item)}`);
      return teile.every((t) => heuhaufen.includes(t));
    })
    .sort((a, b) => (wartet(a) - wartet(b)) || String(a?.dealTitle || '').localeCompare(String(b?.dealTitle || ''), 'de'));
}

/** Eine Ziffernfolge (ab 3 Stellen), zu der kein Deal in der Liste gehört - direkt öffnen. */
export function direkteDealId(suche, items) {
  const roh = text(suche).toLowerCase().replace(/^(d_|#)/, '');
  if (!/^\d{3,}$/.test(roh)) return '';
  return liste(items).some((item) => text(item?.dealId) === roh) ? '' : roh;
}

export function kontaktZeile(item) {
  const kontakt = text(item?.contact);
  const verantwortlich = text(item?.responsible);
  return [kontakt, verantwortlich ? `verantwortlich ${verantwortlich}` : ''].filter(Boolean).join(' · ');
}

// ---------- Verträge und Entwurf ----------

export function erstellbareVertraege(contracts) {
  return liste(contracts).filter((vertrag) => vertrag && vertrag.key !== VERMITTLERVERTRAG);
}

export function vertragZuSchluessel(contracts, schluessel) {
  const k = text(schluessel);
  return liste(contracts).find((vertrag) => vertrag.key === k) || null;
}

export function varianteVon(vertrag, schluessel) {
  if (!vertrag) return null;
  return vertrag.variants.find((variante) => variante.key === schluessel) || vertrag.variants[0] || null;
}

/** Der Arbeitsstand eines Deals am Handy. Vertragsart und Zahlweise der Pacht-Anlage kommen aus dem Deal. */
export function neuerEntwurf({ dealId = '', item = null } = {}) {
  const vertragKey = item ? resolveContractKeyFromPendingDeal(item) : '';
  return {
    dealId: text(dealId),
    vertragKey,
    auto: Boolean(vertragKey),
    variante: 'ci',
    schritt: 1,
    gefuellt: null,
    nachtraege: {},
    klauselIds: [],
    eigentuemer: [],
    anlagen: [],
    lageplan: { quelle: 'vorlage', dataUrl: '', name: '' },
    pacht: {
      an: true,
      vertragKey: resolveKleinspeicherLeaseContractKey(item, KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ),
      werte: { ...LEASE_NUMBER_INITIAL_VALUES },
    },
    vorschau: {},
    geaendert: false,
  };
}

// ---------- Paragraphen und Felder ----------

export function positionsText(position) {
  if (position === 'before-signatures') return 'Vor Unterschriften';
  if (position === 'after-preamble') return 'Nach Präambel';
  return 'Ende des Vertrags';
}

export function verhandlungsKlauselnAus(daten) {
  return Array.isArray(daten?.klauseln) ? daten.klauseln : [];
}

// ---------- Ersetzungen aus dem Gespräch ----------
// Zugestimmte Ersetzungen setzt der Server beim Erzeugen NICHT selbst ein
// (siehe ersetzungenAnwenden in gespraech.js). Am Handy werden sie deshalb vor
// Vorschau und Abschließen auf beide Varianten der Vorlage angewandt.

/** Aus der Sitzungs-Antwort: freigegebene Passus (klauseln) und zugestimmte Ersetzungen. */
export function gespraechAus(daten) {
  const aenderungen = liste(daten?.sitzung?.aenderungen);
  return { klauseln: verhandlungsKlauselnAus(daten), ersetzungen: aenderungen.filter((a) => a?.stand === 'zugestimmt') };
}

function kurzHash(wert) {
  let h = 5381;
  for (let i = 0; i < wert.length; i += 1) h = ((h << 5) + h + wert.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Schlüssel des angewandten Ergebnisses im Speicher - neu, sobald sich eine Ersetzung ändert. '' = nichts anzuwenden. */
export function ersetzungsSchluessel({ dealId, vertragKey, ersetzungen = [] } = {}) {
  if (!vertragKey || !liste(ersetzungen).length) return '';
  return `${text(dealId)}|${vertragKey}|${kurzHash(JSON.stringify(ersetzungen))}`;
}

// ---------- Stand der Vorlage ----------
// Vorlage, Mapping und Paragraphen können sich während einer langen Sitzung
// ändern (Speichern am Handy oder Desktop). Gefüllt wird deshalb mit einem
// Fingerabdruck; weicht der frisch geladene Stand ab, gilt das Füllen nicht mehr.

export const GRUNDLAGE_FRISCH_MS = 60 * 1000;

export function istFrisch(geladenUm, jetzt = Date.now(), grenzeMs = GRUNDLAGE_FRISCH_MS) {
  return Boolean(geladenUm) && jetzt - geladenUm < grenzeMs;
}

export function inhaltsSchluessel(wert) {
  return kurzHash(typeof wert === 'string' ? wert : JSON.stringify(wert ?? null));
}

/** Fingerabdruck aus allen Varianten, Mapping und Paragraphen - '' solange etwas fehlt. */
export function grundlageSchluessel({ vorlagen = null, mapping = null, klauseln = null } = {}) {
  if (!vorlagen || !mapping || !klauseln) return '';
  const html = Object.keys(vorlagen).sort().map((k) => `${k}:${vorlagen[k]}`).join('\n');
  return [inhaltsSchluessel(html), inhaltsSchluessel(mapping), inhaltsSchluessel(klauseln)].join('.');
}

export function fuellenVeraltet({ gefuellt = null, grundlage = '' } = {}) {
  return Boolean(gefuellt && grundlage && gefuellt.grundlage !== grundlage);
}

/** Beide Varianten zusammen: angewandt ist, was in jeder griff; jeder Fehlgrund zählt. */
export function ersetzungenZusammenfassen(ergebnisse = []) {
  const alle = liste(ergebnisse);
  if (!alle.length) return { angewandt: 0, fehlgeschlagen: 0, gruende: [] };
  return {
    angewandt: Math.min(...alle.map((e) => Number(e?.angewandt) || 0)),
    fehlgeschlagen: Math.max(...alle.map((e) => Number(e?.fehlgeschlagen) || 0)),
    gruende: Array.from(new Set(alle.flatMap((e) => liste(e?.gruende)).filter(Boolean))),
  };
}

/** Wie selectedClauses im Studio: Bibliothek in ihrer Reihenfolge, die Gesprächspassus hinten. */
export function gewaehlteKlauseln({ bibliothek = [], ids = [], verhandlung = [] } = {}) {
  const gewaehlt = new Set(liste(ids));
  return [...liste(bibliothek).filter((k) => gewaehlt.has(k.id)), ...liste(verhandlung)];
}

/** Platzhalter der Vorlage plus die der gewählten Paragraphen (effectivePlaceholders im Studio). */
export function effektivePlatzhalter(vorlageHtml, klauseln = []) {
  const gruppen = [extractPlaceholders(vorlageHtml)];
  liste(klauseln).forEach((klausel) => {
    gruppen.push(extractClausePlaceholders(klausel?.content || ''));
    gruppen.push(Object.keys(normalizeClauseDefaultMapping(klausel?.default_mapping || klausel?.defaultMapping)));
  });
  return mergePlaceholders(...gruppen);
}

/** isBuyerPartnerFlow im Studio: Käuferfelder füllt der Bearbeiter, nicht Bitrix. */
export function istKaeuferFluss(vertragKey, platzhalter = []) {
  const kaeufer = new Set(BUYER_FIELD_PLACEHOLDERS);
  return vertragKey === PROJEKTRECHTE || liste(platzhalter).some((p) => kaeufer.has(normalizePlaceholderName(p)));
}

// Spiegel von looksLikeRuntimeFieldToken in api/contracts/usage.js.
function siehtAusWieFeldname(wert) {
  const t = text(wert);
  if (!t || /\s/.test(t) || /^data:/i.test(t)) return false;
  if (/^(DEAL|CONTACT|COMPANY|ASSIGNED_USER)\./i.test(t)) return true;
  if (/^UF_CRM_\d+(?:_[A-Z0-9]+)?$/i.test(t)) return true;
  return /^[A-Z0-9_]+$/.test(t) && t.includes('_');
}

/**
 * Warum ein nachgetragener Wert nicht wörtlich im Vertrag landen würde, sonst
 * ''. Der Server hält Werte ohne Leerzeichen in Großbuchstaben mit Unterstrich
 * für Bitrix-Feldnamen und verwirft Längeres als 1.200 Zeichen.
 */
export function nachtragProblem(wert) {
  const t = text(wert);
  if (!t) return '';
  if (t.length > 1200) return 'Höchstens 1.200 Zeichen – längere Werte übernimmt der Server nicht.';
  if (/^calc\(/i.test(t) || /^data:/i.test(t) || /^\/bitrix\//i.test(t)) return 'So beginnende Werte übernimmt der Server nicht wörtlich.';
  const teile = t.split(/[,\n;]+/).map((teil) => teil.trim()).filter(Boolean);
  if (siehtAusWieFeldname(t) || (teile.length > 1 && teile.every(siehtAusWieFeldname))) {
    return 'Das sieht aus wie ein Bitrix-Feldname und würde nicht wörtlich übernommen. Bitte anders schreiben, etwa mit Leerzeichen.';
  }
  if (t.includes('"')) return 'Gerade Anführungszeichen entfernt der Server. Bitte „…“ verwenden.';
  return '';
}

export function nachtraegeAlsFelder(nachtraege = {}) {
  return Object.fromEntries(Object.entries(nachtraege || {})
    .map(([name, wert]) => [text(name), text(wert)])
    .filter(([name, wert]) => name && wert && name !== LAGEPLAN_PLACEHOLDER));
}

/**
 * mappingFieldsForGeneration des Studios, darüber die Nachträge dieses einen
 * Vertrags. `vertragKey`: leere Felder des Werkvertrags bekommen dessen feste
 * Grundzuordnung (src/lib/werkvertrag.js) – wie im Studio.
 */
export function mappingFuerErzeugung({ mapping = {}, klauseln = [], kaeuferFluss = false, nachtraege = {}, vertragKey = '' } = {}) {
  const basis = removeBuyerFieldsFromMapping(buildMappingFieldsWithClauseDefaults(mapping, klauseln), kaeuferFluss);
  return mitGrundzuordnung(vertragKey, { ...basis, ...nachtraegeAlsFelder(nachtraege) });
}

/** Felder, die nach dem Füllen noch leer sind: nicht aufgelöst und nicht nachgetragen. */
export function leereFelder({ unresolved = [], nachtraege = {}, lageplanAusDatei = false } = {}) {
  return Array.from(new Set(liste(unresolved).map(text).filter(Boolean))).filter((name) => {
    if (name === LAGEPLAN_PLACEHOLDER) return !lageplanAusDatei;
    return !text(nachtraege?.[name]);
  });
}

/** "24 von 27 Feldern gefüllt" */
export function feldZaehler({ platzhalter = [], unresolved = [], nachtraege = {}, lageplanAusDatei = false } = {}) {
  const leer = leereFelder({ unresolved, nachtraege, lageplanAusDatei });
  const gesamt = Math.max(liste(platzhalter).length, new Set(liste(unresolved).map(text).filter(Boolean)).size);
  const gefuellt = Math.max(0, gesamt - leer.length);
  return { gesamt, gefuellt, leer, text: `${gefuellt} von ${gesamt} ${gesamt === 1 ? 'Feld' : 'Feldern'} gefüllt` };
}

// ---------- Lageplan ----------

/**
 * Wendet die Wahl beim Lageplan auf eine Vorlage an: 'datei' bettet das Foto
 * ein, 'deal' setzt den Platzhalter wie "Aus Deal übernehmen" im Studio (der
 * Server holt das Bild dann aus dem Deal), 'vorlage' lässt alles, wie es ist.
 */
export function lageplanAnwenden(html, lageplan) {
  const quelle = lageplan?.quelle;
  if ((quelle !== 'datei' && quelle !== 'deal') || !getLageplanImageInfo(html).available) return html;
  if (quelle === 'datei') {
    return lageplan.dataUrl ? replaceLageplanImageSource(html, lageplan.dataUrl, { fileName: lageplan.name || '' }) : html;
  }
  return replaceLageplanImageSource(html, LAGEPLAN_PLACEHOLDER_TOKEN, { dynamic: true });
}

// ---------- Anlagen ----------

/** Die Prüfung aus handleAttachmentFileChange: Art und Größe. '' = in Ordnung. */
export function anlagenDateiProblem(datei) {
  if (!datei) return 'Keine Datei gewählt.';
  const mime = String(datei.type || '').toLowerCase();
  const passt = mime === 'application/pdf' || mime === 'image/png' || mime === 'image/jpeg'
    || /\.(pdf|png|jpe?g)$/i.test(datei.name || '');
  if (!passt) return 'Bitte für Anlagen nur PDF, PNG oder JPG verwenden.';
  if (datei.size > MAX_CONTRACT_ATTACHMENT_BYTES) {
    return `Die Anlage ist zu groß. Maximal ${(MAX_CONTRACT_ATTACHMENT_BYTES / 1024 / 1024).toFixed(0)} MB pro Datei.`;
  }
  return '';
}

/** Der Eintrag, den handleAttachmentFileChange für eine gelesene Datei anlegt. */
export function anlageAusDatei(zeile, datei, base64) {
  return {
    id: zeile?.id || createAttachmentId(),
    anlageNumber: Number(zeile?.anlageNumber || 0) || null,
    label: String(zeile?.label || datei.name || 'Anlage').trim(),
    filename: datei.name,
    mimeType: datei.type || (datei.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : ''),
    bytes: datei.size,
    base64,
  };
}

/** Ersetzt eine Anlage mit derselben id oder hängt sie an - ohne die alte Liste zu ändern. */
export function setzeAnlage(anlagen, neu) {
  const alt = liste(anlagen);
  return alt.some((a) => a.id === neu.id) ? alt.map((a) => (a.id === neu.id ? neu : a)) : [...alt, neu];
}

/** attachmentRows im Studio: Zeilen des Anlagenverzeichnisses, dahinter eigene Anlagen. */
export function anlagenZeilen({ verweise = [], anlagen = [] } = {}) {
  const ausVerzeichnis = liste(verweise).map((v) => ({
    ...v, attachment: liste(anlagen).find((a) => a.id === v.id) || null, fromAnnexList: true,
  }));
  const ids = new Set(liste(verweise).map((v) => v.id));
  const eigene = liste(anlagen).filter((a) => !ids.has(a.id)).map((a) => ({
    id: a.id, anlageNumber: a.anlageNumber, label: a.label, attachment: a, fromAnnexList: false,
  }));
  return [...ausVerzeichnis, ...eigene];
}

export function naechsteAnlagenNummer(zeilen) {
  return liste(zeilen).reduce((max, z) => Math.max(max, Number(z?.attachment?.anlageNumber ?? z?.anlageNumber) || 0), 0) + 1;
}

export function megabyte(bytes) {
  return (Number(bytes || 0) / 1024 / 1024).toFixed(2).replace('.', ',');
}

// ---------- Anlage Pachtvertrag (nur Kaufvertrag Kleinspeicher) ----------

export function pachtAktiv(entwurf) {
  return entwurf?.vertragKey === KLEINSPEICHER_PURCHASE_CONTRACT_KEY && entwurf?.pacht?.an !== false;
}

export function fehlendePachtzahlen(entwurf) {
  if (!pachtAktiv(entwurf)) return [];
  return getMissingLeaseNumberLabels(entwurf.pacht.werte, getLeaseNumberRequirements([entwurf.pacht.vertragKey]));
}

/** Welche Zahlen die gewählte Zahlweise braucht - Feld, Beschriftung, Beispiel. */
export function pachtFelder(leaseVertragKey) {
  const brauche = getLeaseNumberRequirements([leaseVertragKey]);
  return [
    brauche.requiresAnnual && { feld: 'annualPerKw', label: 'Pacht jährlich/kW', beispiel: 'z. B. 25' },
    brauche.requiresOneTime && { feld: 'oneTimePerKw', label: 'Pacht einmalig/kW', beispiel: 'z. B. 250' },
    brauche.requiresExtensionShare && { feld: 'extensionSharePercent', label: '% in Verlängerung', beispiel: 'z. B. 80' },
  ].filter(Boolean);
}

/**
 * Anlage 8 des Kaufvertrags - dieselbe Form wie buildLeaseAnnexAttachment im
 * Studio. leaseVarianten kommen aus loadAnnexContractVariants, leaseMapping aus
 * loadAnnexContractMappingFields (beide in vertragsStand.js).
 */
export function baueLeaseAnnexAnlage({ leaseVertrag, leaseVarianten = [], leaseMapping = {}, hauptMapping = {}, werte = {}, variantKey = 'ci' } = {}) {
  if (!leaseVertrag) return null;
  const preferred = liste(leaseVarianten).find((entry) => entry.variant.key === variantKey) || liste(leaseVarianten)[0];
  if (!preferred) return null;
  const literalFields = buildLeaseNumberMappingFields(werte, getLeaseNumberRequirements([leaseVertrag.key]));
  return {
    type: 'contract-html',
    key: KLEINSPEICHER_LEASE_ANNEX_KEY,
    contractKey: leaseVertrag.key,
    variantKey: preferred.variant.key,
    label: leaseVertrag.label,
    anlageNumber: KLEINSPEICHER_LEASE_ANNEX_NUMBER,
    templateHtml: preferred.html,
    variantTemplates: liste(leaseVarianten).map((entry) => ({
      variantKey: entry.variant.key,
      variantLabel: entry.variant.label,
      templateHtml: entry.html,
    })),
    mapping: { fields: mergeMappingFieldsPreferFilled(leaseMapping, hauptMapping, literalFields) },
    literalFields,
  };
}

// ---------- Nutzlast für Vorschau und Abschließen ----------

const mitKaeufer = (html, kaeufer, kaeuferFluss) => applyBuyerManualFieldsToHtml(html, kaeufer || {}, kaeuferFluss);

/** Genau der Körper von handleRunPreview (action am-contract-preview). */
export function vorschauPayload({
  vertrag, variante, vorlageHtml, dealId, mappingFelder = {}, klauseln = [], eigentuemer = [], kaeufer = {}, kaeuferFluss = false,
}) {
  return {
    action: 'am-contract-preview',
    contract_key: buildContractStateKey(vertrag.key),
    contract_label: vertrag.label,
    // Das Handy kennt die Wahlbausteine des Werkvertrags nicht: ohne Häkchen ohne sie.
    template_html: wendeWerkvertragOptionenAn(mitKaeufer(vorlageHtml, kaeufer, kaeuferFluss), vertrag.key),
    deal_id: text(dealId),
    template_variant: variante.key,
    variant_label: variante.label,
    mapping: { fields: mappingFelder },
    clauses: liste(klauseln),
    additionalOwners: cleanAdditionalOwnersForPayload(eigentuemer),
  };
}

/** Kurzer Schlüssel einer Vorschau-Anfrage - ändert sich, sobald sich etwas am Ergebnis ändern kann. */
export function vorschauSchluessel(payload) {
  return JSON.stringify([
    payload?.contract_key, payload?.template_variant, payload?.deal_id, inhaltsSchluessel(String(payload?.template_html || '')),
    payload?.mapping, liste(payload?.clauses).map((k) => k?.id || k?.title || ''), payload?.additionalOwners,
  ]);
}

/**
 * Genau der Körper von handleFinalizeContract (am-contract-finalize-enqueue):
 * beide Varianten, die aktive zuerst. vorlagen = { ci: html, druckversion: html }.
 * Mit hochgeladen = { html, dateiname, mitAnderenVarianten } entsteht der Weg
 * "Vertrag bearbeiten" (upload_basis 'uploaded-html'): Die bearbeitete Fassung
 * ist die aktive Variante, die anderen entstehen aus ihr über
 * applyUploadedContractToVariantTemplate.
 */
export function abschlussPayload({
  vertrag, variante, vorlagen = {}, dealId, mappingFelder = {}, klauseln = [], eigentuemer = [],
  anlagen = [], pachtAnlage = null, kaeufer = {}, kaeuferFluss = false, hochgeladen = null,
}) {
  const andere = vertrag.variants.filter((v) => v.key !== variante.key);
  const vorlageVon = (v) => {
    if (typeof vorlagen[v.key] !== 'string' || !vorlagen[v.key]) throw new Error(`Die Vorlage „${v.label}“ ist nicht geladen.`);
    return vorlagen[v.key];
  };
  const eintrag = (v, html) => ({ variant_key: v.key, variant_label: v.label, template_html: html });
  const gemeinsam = {
    mapping: { fields: mappingFelder },
    clauses: liste(klauseln),
    additionalOwners: cleanAdditionalOwnersForPayload(eigentuemer),
    attachments: [...liste(anlagen).map(normalizeAttachmentForPayload).filter(Boolean), ...(pachtAnlage ? [pachtAnlage] : [])],
  };
  const kopf = {
    action: 'am-contract-finalize-enqueue',
    contract_key: buildContractStateKey(vertrag.key),
    contract_label: vertrag.label,
  };
  const deal = { deal_id: text(dealId), template_variant: variante.key, variant_label: variante.label };

  if (hochgeladen) {
    const html = mitKaeufer(hochgeladen.html, kaeufer, kaeuferFluss);
    const geschwister = hochgeladen.mitAnderenVarianten === false ? [] : andere;
    return {
      ...kopf,
      ...deal,
      html,
      upload_basis: 'uploaded-html',
      variant_templates: [
        eintrag(variante, html),
        ...geschwister.map((v) => eintrag(v, mitKaeufer(applyUploadedContractToVariantTemplate(vorlageVon(v), html), kaeufer, kaeuferFluss))),
      ],
      ...gemeinsam,
      source_filename: hochgeladen.dateiname || undefined,
    };
  }

  const aktiv = mitKaeufer(vorlageVon(variante), kaeufer, kaeuferFluss);
  return {
    ...kopf,
    template_html: aktiv,
    ...deal,
    variant_templates: [eintrag(variante, aktiv), ...andere.map((v) => eintrag(v, mitKaeufer(vorlageVon(v), kaeufer, kaeuferFluss)))],
    ...gemeinsam,
  };
}

/** Der Erfolgstext aus handleFinalizeContract. */
export function erfolgsText(vertragsNummer) {
  const nummer = text(vertragsNummer) ? ` (${text(vertragsNummer)})` : '';
  return `Vertrag${nummer} wurde geprüft und in die Upload-Warteschlange gelegt.`;
}

// ---------- Schritte ----------

/** Warum "Weiter" im Schritt nr gesperrt ist, sonst ''. */
export function sperrgrundSchritt(nr, { vertrag = null, gefuellt = false, pachtFehlt = [] } = {}) {
  if (nr === 1 && !vertrag) return 'Erst die Vertragsart wählen.';
  if (nr === 2 && !gefuellt) return 'Erst die Deal-Daten füllen.';
  if (nr === 3 && liste(pachtFehlt).length) return 'Pachtzahlen für die Anlage fehlen.';
  return '';
}

/** Weiter als bis zum ersten unvollständigen Schritt kommt man nicht (Tipp auf den Fortschrittsbalken). */
export function erreichbarerSchritt(ziel, zustand) {
  for (let nr = 1; nr < ziel; nr += 1) {
    if (sperrgrundSchritt(nr, zustand)) return nr;
  }
  return ziel;
}

/**
 * Warum "Abschließen und hochladen" gesperrt ist, sonst ''. Leere Felder
 * sperren, weil der Server einen Vertrag mit offenen Platzhaltern ablehnt
 * (requireNoUnresolved); ein nicht geladenes Gespräch, weil sonst
 * freigegebene Passus still fehlen würden.
 */
export function sperrgrundAbschluss(zustand = {}) {
  // Der Werkliefervertrag geht nie in den Deal (src/lib/werkvertrag.js); das
  // Handy kann nicht herunterladen - also gleich sagen, wo es geht.
  if (istWerkvertrag(zustand.vertrag?.key)) return 'Werkliefervertrag wird nicht hochgeladen – am Rechner im Vertragsstudio als PDF oder HTML herunterladen.';
  for (let nr = 1; nr <= 3; nr += 1) {
    const grund = sperrgrundSchritt(nr, zustand);
    if (grund) return grund;
  }
  if (zustand.gespraechFehlt) return '„Aus dem Gespräch“ ist nicht geladen – erst neu laden.';
  if (zustand.ersetzungenFehlen) return 'Zugestimmte Ersetzungen sind nicht im Vertragstext – erst erneut laden.';
  // Seit 24.10.2026: eine offene Lücke aus dem Gespräch („[Frist Baubeginn]“) geht nie zum Kunden.
  if (zustand.lueckenOffen) return 'Offene Angabe aus dem Gespräch („[…]“) – erst unter KI ausfüllen.';
  const leer = liste(zustand.leer).length;
  if (leer) return `${leer === 1 ? '1 Feld ist' : `${leer} Felder sind`} noch leer – erst nachtragen.`;
  return '';
}

/** Die Zusammenfassung in Schritt 4 - jede Zeile nennt den Schritt, zu dem sie springt. */
export function pruefZeilen({
  vertrag = null, variante = null, zaehler = null, klauseln = [], gespraech = [], ersetzungen = null, lageplanText = '',
  eigentuemer = [], zeilen = [], entwurf = null,
} = {}) {
  const zeile = (label, wert, schritt, warnung = false) => ({ label, wert, schritt, warnung });
  const namen = (werte) => (werte.length ? werte.join(', ') : 'keine');
  const angehaengt = liste(zeilen).filter((z) => z.attachment?.base64).length;
  const ersetzt = Number(ersetzungen?.anzahl) || 0;
  const nichtAnwendbar = Number(ersetzungen?.fehlgeschlagen) || 0;
  const gespraechTeile = [
    liste(gespraech).length ? `${liste(gespraech).length} freigegeben` : '',
    ersetzt ? `${ersetzungenWort(ersetzt)} im Vertragstext` : '',
    nichtAnwendbar ? `${nichtAnwendbar} nicht anwendbar` : '',
  ].filter(Boolean);
  const ergebnis = [
    zeile('Vertrag', vertrag ? `${vertrag.label} · ${variante?.label || 'CI'}` : 'nicht gewählt', 1, !vertrag),
    zeile('Deal-Daten', !zaehler ? 'noch nicht gefüllt'
      : (zaehler.leer.length ? `${zaehler.text} · ${zaehler.leer.join(', ')} leer` : zaehler.text), 2, !zaehler || zaehler.leer.length > 0),
    zeile('Optionale Paragraphen', namen(liste(klauseln).map((k) => k.title || 'Paragraph')), 3),
    zeile('Aus dem Gespräch', gespraechTeile.length ? gespraechTeile.join(' · ') : 'keine', 3, nichtAnwendbar > 0),
  ];
  if (lageplanText) ergebnis.push(zeile('Lageplan', lageplanText, 3));
  ergebnis.push(
    zeile('Weitere Eigentümer', namen(liste(eigentuemer).map((e) => text(e.name) || text(e.company) || 'ohne Namen')), 3),
    zeile('PDF-Anlagen', angehaengt ? `${angehaengt} von ${liste(zeilen).length} angehängt` : 'keine angehängt', 3),
  );
  if (entwurf?.vertragKey === KLEINSPEICHER_PURCHASE_CONTRACT_KEY) {
    const fehlt = fehlendePachtzahlen(entwurf);
    const zahlweise = entwurf.pacht.vertragKey === KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ ? 'Einmalzahlung' : 'Jahreszahlung';
    let wert = 'nicht angehängt';
    if (pachtAktiv(entwurf)) wert = fehlt.length ? `fehlt: ${fehlt.join(', ')}` : `${zahlweise} · wird angehängt`;
    ergebnis.push(zeile('Anlage Pachtvertrag', wert, 3, fehlt.length > 0));
  }
  return ergebnis;
}
